import { Drawer } from '@mui/material';
import Box from '@mui/material/Box';
import React from 'react';
import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useGetTransactionsQuery } from '@/api/v1';
import { NoResultsView } from '@/components/atoms/NoResultsView';
import { ActivitySearchBar } from '@/components/organisms/ActivitySearchBar';
import { MainScreen } from '@/components/organisms/MainScreen';
import { TransactionsTable } from '@/components/organisms/TransactionsTable';
import { useCustomer } from '@/hooks/useCustomer';
import { useTranslator } from '@/hooks/useTranslator';
import { useTypedSearchParams } from '@/hooks/useTypedSearchParams';
import { NoActivityView } from '@/Screens/Dashboard/Activity/NoActivityView';
import { GetTransactionsFilters } from '@/types/getTransactionsFilters';
import { ALL_ACTIVITY_COLUMNS } from '@/types/transactionColumn';
import { ALL_ACTIVITY_FILTERS } from '@/types/transactionFilterField';
import { countTransactionFilters } from '@/utility/countTransactionFilters';

export const Activity: React.FC = (): React.ReactElement => {
  const [search] = useSearchParams();
  const [filters, setFilters] = useTypedSearchParams<GetTransactionsFilters>(
    GetTransactionsFilters.initial(),
  );

  const tr = useTranslator();
  const customer = useCustomer();
  const navigate = useNavigate();

  const { transactionId = '' } = useParams<{ readonly transactionId: string | undefined }>();

  const queryFilters = React.useMemo(
    (): Partial<GetTransactionsFilters> => ({
      ...filters,
      beneficiaryId: filters.beneficiaryId || undefined,
    }),
    [filters],
  );

  const { data: transactionsPage } = useGetTransactionsQuery(queryFilters, {
    skip: !customer.beneficiaryId,
  });

  const hasNoTransactions = !transactionsPage || transactionsPage.content.length === 0;

  const onRowClick = React.useCallback(
    (transactionId: string): void => {
      navigate({
        pathname: transactionId,
        search: search.toString(),
      });
    },
    [navigate, search],
  );

  const closeTransactionDetails = React.useCallback((): void => {
    navigate({
      pathname: '.',
      search: search.toString(),
    });
  }, [navigate, search]);

  const handleFiltersChange = React.useCallback(
    (newFilters: GetTransactionsFilters): void => {
      setFilters(newFilters);
    },
    [setFilters],
  );

  const filtersCount = React.useMemo((): number => countTransactionFilters(filters), [filters]);

  return (
    <MainScreen title={tr('ACTIVITY')}>
      <Box display="flex" flexDirection="column" gap={4} height="100%">
        <ActivitySearchBar
          filtersCount={filtersCount}
          visibleFilters={ALL_ACTIVITY_FILTERS}
          isDownloadDisabled={hasNoTransactions}
        />
        <TransactionsTable
          beneficiaryId={null}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onRowClick={onRowClick}
          noContentView={<NoActivityView />}
          noResultsView={
            <NoResultsView
              title={tr('ZERO_RESULTS_FOUND')}
              message={tr('TRY_CHANGING_YOUR_APPLIED_FILTERS', tr('ACTIVITY').toLocaleLowerCase())}
            />
          }
          visibleColumns={ALL_ACTIVITY_COLUMNS}
        />
      </Box>

      <Drawer open={!!transactionId} anchor="right" onClose={closeTransactionDetails}>
        <Outlet />
      </Drawer>
    </MainScreen>
  );
};
